import { RangedPresetValue } from './Types';
import { localMoment, timeZoneLessLocalMoment } from 'util/Time';

export const RangedValueDelimiter: string = ',';
export const MultipleValueDelimiter: string = '[~]';
export const ValueLabelDelimiter: string = '[:]';
export const HierarchyDelimiter: string = ' > ';
export const NullValue: string = '[NULL]';
export const NotNullValue: string = '[NOT_NULL]';
export const NullValueLabel: string = '<No Value>';
export const NotNullValueLabel: string = '<Any Value>';

export interface RangePresetValuePresets {
	[index: string]: (ignoreTimezone?: boolean) => RangedPresetValue;
}

/**
 * Date range presets used by the date range preset filter type
 */
export const MultipleDateRangePresets: RangePresetValuePresets = {
	Today: (ignoreTimezone?: boolean): RangedPresetValue => ({
		fromValue: ignoreTimezone
			? timeZoneLessLocalMoment(localMoment().startOf('day'))
			: localMoment().startOf('day'),
		toValue: ignoreTimezone ? timeZoneLessLocalMoment(localMoment().endOf('day')) : localMoment().endOf('day'),
		preset: 'Today',
		displayName: 'Today',
		axisPrecision: 'day',
	}),
	Yesterday: (ignoreTimezone?: boolean): RangedPresetValue => ({
		fromValue: ignoreTimezone
			? timeZoneLessLocalMoment(localMoment().subtract(1, 'days').startOf('day'))
			: localMoment().subtract(1, 'days').startOf('day'),
		toValue: ignoreTimezone
			? timeZoneLessLocalMoment(localMoment().subtract(1, 'days').endOf('day'))
			: localMoment().subtract(1, 'days').endOf('day'),
		preset: 'Yesterday',
		displayName: 'Yesterday',
		axisPrecision: 'day',
	}),
	WTD: (ignoreTimezone?: boolean): RangedPresetValue => ({
		fromValue: ignoreTimezone
			? timeZoneLessLocalMoment(localMoment().startOf('week'))
			: localMoment().startOf('week'),
		toValue: ignoreTimezone ? timeZoneLessLocalMoment(localMoment()) : localMoment(),
		preset: 'WTD',
		displayName: 'WTD',
		axisPrecision: 'day',
	}),
	LastWeek: (ignoreTimezone?: boolean): RangedPresetValue => ({
		fromValue: ignoreTimezone
			? timeZoneLessLocalMoment(localMoment().subtract(1, 'week').startOf('week'))
			: localMoment().subtract(1, 'week').startOf('week'),
		toValue: ignoreTimezone
			? timeZoneLessLocalMoment(localMoment().subtract(1, 'week').endOf('week'))
			: localMoment().subtract(1, 'week').endOf('week'),
		preset: 'LastWeek',
		displayName: 'Last Week',
		axisPrecision: 'day',
	}),
	MTD: (ignoreTimezone?: boolean): RangedPresetValue => ({
		fromValue: ignoreTimezone
			? timeZoneLessLocalMoment(localMoment().startOf('month'))
			: localMoment().startOf('month'),
		toValue: ignoreTimezone ? timeZoneLessLocalMoment(localMoment()) : localMoment(),
		preset: 'MTD',
		displayName: 'MTD',
		axisPrecision: 'week',
	}),
	LastMonth: (ignoreTimezone?: boolean): RangedPresetValue => ({
		fromValue: ignoreTimezone
			? timeZoneLessLocalMoment(localMoment().subtract(1, 'month').startOf('month'))
			: localMoment().subtract(1, 'month').startOf('month'),
		toValue: ignoreTimezone
			? timeZoneLessLocalMoment(localMoment().subtract(1, 'month').endOf('month'))
			: localMoment().subtract(1, 'month').endOf('month'),
		preset: 'LastMonth',
		displayName: 'Last Month',
		axisPrecision: 'week',
	}),
	Last60Days: (ignoreTimezone?: boolean): RangedPresetValue => ({
		fromValue: ignoreTimezone
			? timeZoneLessLocalMoment(localMoment().subtract(60, 'days'))
			: localMoment().subtract(60, 'days'),
		toValue: ignoreTimezone ? timeZoneLessLocalMoment(localMoment()) : localMoment(),
		preset: 'Last60Days',
		displayName: 'Last 60 Days',
		axisPrecision: 'week',
	}),
	Last90Days: (ignoreTimezone?: boolean): RangedPresetValue => ({
		fromValue: ignoreTimezone
			? timeZoneLessLocalMoment(localMoment().subtract(90, 'days'))
			: localMoment().subtract(90, 'days'),
		toValue: ignoreTimezone ? timeZoneLessLocalMoment(localMoment()) : localMoment(),
		preset: 'Last90Days',
		displayName: 'Last 90 Days',
		axisPrecision: 'week',
	}),
	YTD: (ignoreTimezone?: boolean): RangedPresetValue => ({
		fromValue: ignoreTimezone
			? timeZoneLessLocalMoment(localMoment().startOf('year'))
			: localMoment().startOf('year'),
		toValue: ignoreTimezone ? timeZoneLessLocalMoment(localMoment()) : localMoment(),
		preset: 'YTD',
		displayName: 'YTD',
		axisPrecision: 'month',
	}),
	LastYear: (ignoreTimezone?: boolean): RangedPresetValue => ({
		fromValue: ignoreTimezone
			? timeZoneLessLocalMoment(localMoment().subtract(1, 'years').startOf('year'))
			: localMoment().subtract(1, 'years').startOf('year'),
		toValue: ignoreTimezone
			? timeZoneLessLocalMoment(localMoment().startOf('year'))
			: localMoment().startOf('year'),
		preset: 'LastYear',
		displayName: 'Last Year',
		axisPrecision: 'month',
	}),
	AllTime: (ignoreTimezone?: boolean): RangedPresetValue => ({
		fromValue: ignoreTimezone
			? timeZoneLessLocalMoment(localMoment().subtract(1000, 'years'))
			: localMoment().subtract(1000, 'years'),
		toValue: ignoreTimezone ? timeZoneLessLocalMoment(localMoment()) : localMoment(),
		preset: 'AllTime',
		displayName: 'All Time',
		axisPrecision: 'month',
	}),
};

/**
 * Returns a friendly string containing a single ranged preset value
 * @param value
 */
export const getFriendlyDateRangePresetValueForDisplay = (value: RangedPresetValue) => {
	if (value.preset) {
		return value.displayName || value.preset;
	}

	const dateFormat = 'MM-DD-YY';
	const fromTime = value.fromValue ? localMoment(value.fromValue).format(dateFormat) : '';
	const toTime = value.toValue ? localMoment(value.toValue).format(dateFormat) : '';

	if (value.fromValue && value.toValue && toTime === fromTime) {
		return `On ${toTime}`;
	} else if (value.fromValue && value.toValue) {
		return `Between ${fromTime} and ${toTime}`;
	} else if (value.fromValue) {
		return `After ${fromTime}`;
	} else if (value.toValue) {
		return `Before ${toTime}`;
	}

	return '<Empty>';
};

/**
 * Date range presets used by the single date picker preset filter type
 */
export const SingleDateRangePresets: RangePresetValuePresets = {
	Today: (): RangedPresetValue => ({
		fromValue: localMoment().startOf('day'),
		toValue: null,
		preset: 'Today',
		displayName: 'Today',
	}),
	Yesterday: (): RangedPresetValue => ({
		fromValue: localMoment().subtract(1, 'days').startOf('day'),
		toValue: null,
		preset: 'Yesterday',
		displayName: 'Yesterday',
	}),
	LastWeek: (): RangedPresetValue => ({
		fromValue: localMoment().subtract(1, 'week').startOf('week'),
		toValue: null,
		preset: 'LastWeek',
		displayName: 'Last Week',
	}),
	LastMonth: (): RangedPresetValue => ({
		fromValue: localMoment().subtract(1, 'month').startOf('month'),
		toValue: null,
		preset: 'LastMonth',
		displayName: 'Last Month',
	}),
	Last60Days: (): RangedPresetValue => ({
		fromValue: localMoment().subtract(60, 'days'),
		toValue: null,
		preset: 'Last60Days',
		displayName: 'Last 60 Days',
	}),
	Last90Days: (): RangedPresetValue => ({
		fromValue: localMoment().subtract(90, 'days'),
		toValue: null,
		preset: 'Last90Days',
		displayName: 'Last 90 Days',
	}),
	LastYear: (): RangedPresetValue => ({
		fromValue: localMoment().subtract(1, 'years').startOf('year'),
		toValue: null,
		preset: 'LastYear',
		displayName: 'Last Year',
	}),
};

export const Channel: {
	All: string;
} = {
	All: 'All',
};
